import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import type { RootState } from '../index';

// Типы для статуса
export interface Status {
  id: string;
  name: string;
  is_positive: boolean;
  created_at: string;
}

// Типы для состояния статусов
interface StatusesState {
  statuses: Status[];
  loading: boolean;
  error: string | null;
}

// Асинхронные действия
export const fetchStatuses = createAsyncThunk(
  'statuses/fetchStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getStatuses();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch statuses');
    }
  }
);

export const changeProductStatus = createAsyncThunk(
  'statuses/changeProductStatus',
  async ({ productId, statusId }: { productId: string; statusId: string }, { rejectWithValue, getState }) => {
    try {
      const response = await api.changeProductStatus(productId, statusId);
      
      // Получаем информацию о статусе из state
      const state = getState() as RootState;
      const status = state.statuses.statuses.find(s => s.id === statusId);
      
      return {
        productId,
        statusId,
        statusName: status?.name || null,
        statusIsPositive: status?.is_positive || false,
        message: response.message,
        generatedArticle: response.generatedArticle
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to change product status');
    }
  }
);

export const changeProductStatusBatch = createAsyncThunk(
  'statuses/changeProductStatusBatch',
  async ({ productIds, statusId }: { productIds: string[]; statusId: string }, { rejectWithValue, getState }) => {
    try {
      const response = await api.changeProductStatusBatch(productIds, statusId);
      
      // Получаем информацию о статусе из state
      const state = getState() as RootState;
      const status = state.statuses.statuses.find(s => s.id === statusId);
      
      // Обрабатываем результаты и возвращаем массив обновлений
      const updates = response.data.results
        .filter((result: any) => result.success)
        .map((result: any) => ({
          productId: result.productId,
          statusId,
          statusName: status?.name || null,
          statusIsPositive: status?.is_positive || false,
          message: result.message,
          generatedArticle: result.generatedArticle || null
        }));
      
      const errors = response.data.results
        .filter((result: any) => !result.success)
        .map((result: any) => ({
          productId: result.productId,
          error: result.message
        }));
      
      return {
        updates,
        errors,
        summary: response.data.summary
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to change product statuses');
    }
  }
);

// Начальное состояние
const initialState: StatusesState = {
  statuses: [],
  loading: false,
  error: null,
};

// Слайс статусов
const statusesSlice = createSlice({
  name: 'statuses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch statuses
      .addCase(fetchStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatuses.fulfilled, (state, action) => {
        state.loading = false;
        state.statuses = action.payload.data || [];
      })
      .addCase(fetchStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Change product status
      .addCase(changeProductStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeProductStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Здесь мы не обновляем локальное состояние, так как товары хранятся в productsSlice
        // Вместо этого мы будем обновлять товары в productsSlice
      })
      .addCase(changeProductStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Change product status batch
      .addCase(changeProductStatusBatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeProductStatusBatch.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Здесь мы не обновляем локальное состояние, так как товары хранятся в productsSlice
        // Вместо этого мы будем обновлять товары в productsSlice
      })
      .addCase(changeProductStatusBatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setLoading } = statusesSlice.actions;
export default statusesSlice.reducer;
